// AdministrativeJobs.jsx
import React from 'react';
import { Settings, DollarSign, Heart, Shield, GraduationCap } from 'lucide-react';
import './AdministrativeJobs.css';

const AdministrativeJobs = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleJobPositionChange = (index, value) => {
    const positions = data.administrativePositions || Array(7).fill('');
    positions[index] = value;
    onChange('administrativePositions', positions);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Settings className="section-icon" size={32} /> */}
        <h2>خامساً: الوظائف الإدارية والبيانات الشخصية المطلوبة</h2>
      </div>
      
      {/* Administrative Positions List */}
      <div className="">
        <div className="question-header">
          <span className="question-number">16</span>
          <label className="question-title">اذكر مسمى الوظائف الإدارية الموجودة في شركتكم؟</label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Settings className="category-icon" size={24} />
            <h4>الوظائف الإدارية:</h4>
          </div> */}
          
          <div className="positions-grid">
            {[0, 1, 2, 3, 4, 5, 6].map(index => (
              <div key={index} className="position-input-group administrative-position">
                <label className="position-label" htmlFor={`admin-position-${index}`}>
                  الوظيفة {String(index + 1).padStart(2, '0')}:
                </label>
                <input
                  id={`admin-position-${index}`}
                  type="text"
                  value={(data.administrativePositions || [])[index] || ''}
                  onChange={(e) => handleJobPositionChange(index, e.target.value)}
                  placeholder="(مثل: مدير المشاريع، منسق العمليات، مساعد إداري...)"
                  className="position-input"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Personal Data Requirements */}
      <div className="">
        <div className="question-header">
          <span className="question-number">17</span>
          <label className="question-title">
            حدد البيانات الشخصية التي تطلبها شركتكم من المترشحين للتعيين على الوظائف الإدارية؟ 
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        {/* Financial Data Section */}
        <div className="data-category" data-field="adminFinancialData">
          <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>البيانات المالية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('adminFinancialData')}`}>
            {[
              'الراتب السابق',
              'الحالة الائتمانية',
              'حسابات البنوك',
              'بيانات التأمينات الاجتماعية',
              'الالتزامات المالية والديون',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item admin-financial-item">
                <input
                  type="checkbox"
                  checked={(data.adminFinancialData || []).includes(item)}
                  onChange={() => handleMultiSelect('adminFinancialData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('adminFinancialData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-admin-financial">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-admin-financial"
              type="text"
              value={data.otherAdminFinancialData || ''}
              onChange={(e) => handleOtherFieldChange('otherAdminFinancialData', e.target.value)}
              placeholder="بيانات مالية أخرى للوظائف الإدارية..."
              className={`other-input ${getErrorClass('otherAdminFinancialData')}`}
            />
            {renderError('otherAdminFinancialData')}
          </div>
        </div>

        {/* Health Data Section */}
        <div className="data-category" data-field="adminHealthData">
          <div className="category-header">
            <Heart className="category-icon" size={24} />
            <h4>البيانات الصحية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('adminHealthData')}`}>
            {[
              'الفحوصات الطبية العامة',
              'فحوصات طبية متخصصة',
              'تاريخ الأمراض المزمنة',
              'فحص السموم',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item admin-health-item">
                <input
                  type="checkbox"
                  checked={(data.adminHealthData || []).includes(item)}
                  onChange={() => handleMultiSelect('adminHealthData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('adminHealthData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-admin-health">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-admin-health"
              type="text"
              value={data.otherAdminHealthData || ''}
              onChange={(e) => handleOtherFieldChange('otherAdminHealthData', e.target.value)}
              placeholder="بيانات صحية أخرى للوظائف الإدارية..."
              className={`other-input ${getErrorClass('otherAdminHealthData')}`}
            />
            {renderError('otherAdminHealthData')}
          </div>
        </div>

        {/* Legal and Security Data Section */}
        <div className="data-category" data-field="adminLegalData">
          <div className="category-header">
            <Shield className="category-icon" size={24} />
            <h4>البيانات القانونية والأمنية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('adminLegalData')}`}>
            {[
              'قضايا قانونية سابقة',
              'تصاريح أمنية سابقة',
              'سجل القيادة',
              'السجل الجنائي المفصل',
              'التحقيقات الأمنية',
              'سجل السفر',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item admin-security-item">
                <input
                  type="checkbox"
                  checked={(data.adminLegalData || []).includes(item)}
                  onChange={() => handleMultiSelect('adminLegalData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('adminLegalData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-admin-legal">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-admin-legal"
              type="text"
              value={data.otherAdminLegalData || ''}
              onChange={(e) => handleOtherFieldChange('otherAdminLegalData', e.target.value)}
              placeholder="بيانات قانونية وأمنية أخرى للوظائف الإدارية..."
              className={`other-input ${getErrorClass('otherAdminLegalData')}`}
            />
            {renderError('otherAdminLegalData')}
          </div>
        </div>

        {/* Academic and Professional Data Section */}
        <div className="data-category" data-field="adminAcademicData">
          <div className="category-header">
            <GraduationCap className="category-icon" size={24} />
            <h4>البيانات الأكاديمية والمهنية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('adminAcademicData')}`}>
            {[
              'المؤهلات المهنية المعتمدة',
              'الخبرات التخصصية السابقة',
              'التراخيص المهنية',
              'خطابات التوصية',
              'تقييمات الأداء السابقة',
              'أسماء الشركات والوظائف السابقة',
              'أسباب ترك الوظائف السابقة',
              'الراتب السابق',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item admin-academic-item">
                <input
                  type="checkbox"
                  checked={(data.adminAcademicData || []).includes(item)}
                  onChange={() => handleMultiSelect('adminAcademicData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('adminAcademicData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-admin-academic">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-admin-academic"
              type="text"
              value={data.otherAdminAcademicData || ''}
              onChange={(e) => handleOtherFieldChange('otherAdminAcademicData', e.target.value)}
              placeholder="بيانات أكاديمية ومهنية أخرى للوظائف الإدارية..."
              className={`other-input ${getErrorClass('otherAdminAcademicData')}`}
            />
            {renderError('otherAdminAcademicData')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministrativeJobs;